import { FC, PropsWithChildren, createContext, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@/components/lib/components/Box';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

import { InfoSheet } from './InfoSheet';
import { Modal } from './Modal';

const MIN_DISPLAY_TIME = 2000;

export type ValidationSheet = {
    showValidationSheet: () => void;
    dismissValidationSheet: () => void;
};

export const ValidationSheetContext = createContext<ValidationSheet | undefined>(undefined);

export const useValidationSheet = () => {
    const context = useContext(ValidationSheetContext);
    if (context === undefined) {
        throw new Error('useValidationSheet must be used within a ValidationSheetProvider');
    }
    return context;
};

export const ValidationSheetProvider: FC<PropsWithChildren> = ({ children }) => {
    const validationSheetRef = useRef<BottomSheetModalMethods | null>(null);
    const { t } = useTranslation(['betslip_pickem', 'common']);
    const lastShown = useRef<number>(0);

    const validationSheet = useMemo(
        () => ({
            showValidationSheet: () => {
                validationSheetRef.current?.present();
                lastShown.current = Date.now();
            },
            dismissValidationSheet: () => {
                // ensure the sheet is shown for at least 2 seconds before dismissing
                const elapsed = Date.now() - lastShown.current;
                if (elapsed < MIN_DISPLAY_TIME) {
                    setTimeout(() => {
                        validationSheetRef.current?.dismiss();
                    }, MIN_DISPLAY_TIME - elapsed);
                } else {
                    validationSheetRef.current?.dismiss();
                }
            },
        }),
        []
    );

    return (
        <ValidationSheetContext.Provider value={validationSheet}>
            <Box flex={1} overflow={'hidden'}>
                {children}
                <Modal id="validation-sheet" modalRef={validationSheetRef} dismissible={true}>
                    <InfoSheet
                        modalRef={validationSheetRef}
                        title={t('validating_lineup')}
                        description={t('retry')}
                        buttonLabel={t('common:dismiss')}
                    />
                </Modal>
            </Box>
        </ValidationSheetContext.Provider>
    );
};
